import { WebContainer } from '@webcontainer/api';
import { atom, map, type MapStore } from 'nanostores';
import * as nodePath from 'node:path';
import type { ActionAlert, AlphaAction } from '~/types/actions';
import { createScopedLogger } from '~/utils/logger';
import { unreachable } from '~/utils/unreachable';
import type { ActionCallbackData } from './message-parser';
import type { DualiteShell } from '~/utils/shell';
import { TerminalErrorEmitter } from '~/utils/terminal/TerminalErrorEmitter';
import { ErrorBuffer } from '~/utils/terminal/ErrorBuffer';

const logger = createScopedLogger('ActionRunner');

export type ActionStatus = 'pending' | 'running' | 'complete' | 'aborted' | 'failed';

export type BaseActionState = AlphaAction & {
  status: Exclude<ActionStatus, 'failed'>;
  abort: () => void;
  executed: boolean;
  abortSignal: AbortSignal;
};

export type FailedActionState = AlphaAction &
  Omit<BaseActionState, 'status'> & {
    status: Extract<ActionStatus, 'failed'>;
    error: string;
  };

export type ActionState = BaseActionState | FailedActionState;

type BaseActionUpdate = Partial<Pick<BaseActionState, 'status' | 'abort' | 'executed'>>;

export type ActionStateUpdate =
  | BaseActionUpdate
  | (Omit<BaseActionUpdate, 'status'> & { status: 'failed'; error: string });

type ActionsMap = MapStore<Record<string, ActionState>>;

class ActionCommandError extends Error {
  readonly _output: string;
  readonly _header: string;

  constructor(message: string, output: string) {
    // Create a formatted message that includes both the error message and output
    const formattedMessage = `Failed To Execute Shell Command: ${message}\n\nOutput:\n${output}`;
    super(formattedMessage);

    // Set the output separately so it can be accessed programmatically
    this._header = message;
    this._output = output;

    // Maintain proper prototype chain
    Object.setPrototypeOf(this, ActionCommandError.prototype);

    // Set the name of the error for better debugging
    this.name = 'ActionCommandError';
  }

  // Optional: Add a method to get just the terminal output
  get output() {
    return this._output;
  }
  get header() {
    return this._header;
  }
}

export class ActionRunner {
  #webcontainer: Promise<WebContainer>;
  #currentExecutionPromise: Promise<void> = Promise.resolve();
  #shellTerminal: () => DualiteShell;
  #errorBuffer = new ErrorBuffer();
  #errorEmitter = new TerminalErrorEmitter(this.#errorBuffer);
  runnerId = atom<string>(`${Date.now()}`);
  actions: ActionsMap = map({});
  onAlert?: (alert: ActionAlert) => void;

  constructor(
    webcontainerPromise: Promise<WebContainer>,
    getShellTerminal: () => DualiteShell,
    onAlert?: (alert: ActionAlert) => void,
  ) {
    this.#webcontainer = webcontainerPromise;
    this.#shellTerminal = getShellTerminal;
    this.onAlert = onAlert;
  }
  addAction(data: ActionCallbackData) {
    console.log('addAction action-runner', data);

    const { actionId } = data;

    const actions = this.actions.get();
    const action = actions[actionId];

    if (action) {
      console.log('action already added');
      return;
    }

    const abortController = new AbortController();

    this.actions.setKey(actionId, {
      ...data.action,
      status: 'pending',
      executed: false,
      abort: () => {
        abortController.abort();
        this.#updateAction(actionId, { status: 'aborted' });
      },
      abortSignal: abortController.signal,
    });

    console.log('Action map after adding: ', this.actions.get());

    this.#currentExecutionPromise.then(() => {
      this.#updateAction(actionId, { status: 'running' });
    });
  }

  addHistoryAction(data: ActionCallbackData) {
    console.log('addHistoryAction action-runner', data);

    const { actionId } = data;

    const actions = this.actions.get();
    const action = actions[actionId];

    if (action) {
      console.log('action already added - this should not happen for history actions');
      return;
    }

    const abortController = new AbortController();

    console.log('[addHistoryAction] action', data.action);
    console.log('[addHistoryAction] action content: ', data.action.content);
    console.log(
      '[addHistoryAction] action content length: ',
      typeof data.action.content === 'string' ? data.action.content.length : 'not a string',
    );

    const baseActionState = {
      content: data.action.content,
      type: data.action.type,
      status: 'complete' as const,
      executed: true,
      abort: () => {
        abortController.abort();
        this.#updateAction(actionId, { status: 'aborted' });
      },
      abortSignal: abortController.signal,
    };

    this.actions.setKey(
      actionId,
      data.action.type === 'file'
        ? { ...baseActionState, type: 'file' as const, filePath: data.action.filePath }
        : { ...baseActionState, type: 'shell' as const },
    );
  }

  async runAction(data: ActionCallbackData) {
    const { actionId } = data;
    console.log('runAction action-runner', data);

    const action = this.actions.get()[actionId];

    // console.warn('runAction action-runner: ', this.actions.get());
    if (!action) {
      unreachable(`Action ${actionId} not found`);
    }

    if (action.executed) {
      return;
    }

    this.#updateAction(actionId, { ...action, ...data.action, executed: true });

    this.#currentExecutionPromise = this.#currentExecutionPromise
      .then(() => {
        return this.#executeAction(actionId);
      })
      .catch((error) => {
        /*
         * Sentry.captureException(error, {
         *   extra: {
         *     message: 'Error from action-runner',
         *     actionId,
         *     action,
         *   },
         *   level: 'log',
         * });
         */
        console.error('Action failed:', error);
      });
  }

  async #executeAction(actionId: string) {
    const action = this.actions.get()[actionId];

    // console.log('execute action-runner action', action);

    this.#updateAction(actionId, { status: 'running' });

    try {
      switch (action.type) {
        case 'shell': {
          await this.#runShellAction(action);
          break;
        }
        case 'file': {
          await this.#runFileAction(action);
          break;
        }
      }

      this.#updateAction(actionId, { status: action.abortSignal.aborted ? 'aborted' : 'complete' });
    } catch (error) {
      console.error('Action failed:', error);
      this.#updateAction(actionId, { status: 'failed', error: 'Action failed' });

      throw error;
    }
  }
  async #runShellAction(action: ActionState) {
    if (action.type !== 'shell') {
      unreachable('Expected shell action');
    }

    const shell = this.#shellTerminal();
    await shell.ready();

    console.log('[terminal] Running shell action:', shell);

    if (!shell || !shell.terminal || !shell.process) {
      unreachable('Shell terminal not found');
    }

    // Check if the command contains a pipe
    const isPipedCommand = (action.content as string).includes('|');

    console.log('isPipedCommand', isPipedCommand);

    const resp = await shell.executeCommand(
      this.runnerId.get(),
      (action.content as string) + ' #ai', // to differentiate between manual and ai commands
      () => {
        logger.debug(`[${action.type}]:Aborting Action\n\n`, action);
        action.abort();
      },
      isPipedCommand,
    );

    logger.debug(`${action.type} Shell Response: [exit code:${resp?.exitCode}]`);

    if (resp?.exitCode != 0) {
      // Use TerminalErrorEmitter with improved error formatting
      console.log('resp-error', resp);

      try {
        // Clean the output for better error messaging
        const output = resp?.output || 'No Output Available';
        const cleanedOutput = this.#errorBuffer.cleanTerminalForErrors(output);
        const errorMessage = this.#errorBuffer.extractErrorMessage(output);

        // Emit a more readable error
        this.#errorEmitter.emitTerminalError(cleanedOutput, resp?.exitCode || 1, action.content as string);
      } catch (error) {
        console.error('[ActionRunner] Error emitting terminal error:', error);
      }

      throw new ActionCommandError(`Failed To Execute Shell Command`, resp?.output || 'No Output Available');
    }
  }
  async #runFileAction(action: ActionState) {
    if (action.type !== 'file') {
      unreachable('Expected file action');
    }

    const webcontainer = await this.#webcontainer;

    let folder = nodePath.dirname(action.filePath);

    // remove trailing slashes
    folder = folder.replace(/\/+$/g, '');

    if (folder !== '.') {
      try {
        await webcontainer.fs.mkdir(folder, { recursive: true });
        logger.debug('Created folder', folder);
      } catch (error) {
        /*
         * Sentry.captureException(error, {
         *   extra: {
         *     message: 'Error from action-runner in folder creation',
         *     folder,
         *   },
         *   level: 'log',
         * });
         */
        logger.error('Failed to create folder\n\n', error);
      }
    }

    type BufferEncoding =
      | 'ascii'
      | 'utf8'
      | 'utf-8'
      | 'utf16le'
      | 'ucs2'
      | 'ucs-2'
      | 'base64'
      | 'base64url'
      | 'latin1'
      | 'binary'
      | 'hex';

    try {
      let options: { encoding?: BufferEncoding | null } | null = null;
      let content = action.content;

      // TODO: change this to use the isAssetFile function
      const isBinaryFile =
        action.filePath.endsWith('.png') ||
        action.filePath.endsWith('.jpg') ||
        action.filePath.endsWith('.jpeg') ||
        action.filePath.endsWith('.gif') ||
        action.filePath.endsWith('.ico') ||
        action.filePath.endsWith('.webp') ||
        action.filePath.endsWith('.bmp') ||
        action.filePath.endsWith('.tiff') ||
        action.filePath.endsWith('.mp3') ||
        action.filePath.endsWith('.mp4') ||
        action.filePath.endsWith('.wav') ||
        action.filePath.endsWith('.avi') ||
        action.filePath.endsWith('.mov') ||
        action.filePath.endsWith('.wmv') ||
        action.filePath.endsWith('.flv') ||
        action.filePath.endsWith('.webm') ||
        action.filePath.endsWith('.m4a') ||
        action.filePath.endsWith('.m4v') ||
        action.filePath.endsWith('.ogg') ||
        action.filePath.endsWith('.ogv') ||
        action.filePath.endsWith('.webm');

      if (isBinaryFile) {
        console.log('[api] inside file written', action.filePath);
        options = { encoding: 'base64' };

        // If content is a data URL, extract just the base64 part
        if (typeof content === 'string' && content.startsWith('data:')) {
          content = content.split(',')[1];
        }
      }

      console.log('[api] after file written', action.filePath, content, options);

      const result = await webcontainer.fs.writeFile(action.filePath, content as string, options);
      console.log('[api] File written', result, action.filePath, content, options, result);
      logger.debug(`File written ${action.filePath}`);
    } catch (error) {
      /*
       * Sentry.captureException(error, {
       *   extra: {
       *     message: 'Error from action-runner in writing a file',
       *     filePath: action.filePath,
       *   },
       *   level: 'log',
       * });
       */
      logger.error('Failed to write file\n\n', error);
    }
  }

  #updateAction(id: string, newState: ActionStateUpdate) {
    const actions = this.actions.get();

    this.actions.setKey(id, { ...actions[id], ...newState });
  }
}
